import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

/** A side drawer (mobile navigation, section lists). Built on the accessible Radix dialog. */
export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetClose = DialogPrimitive.Close;
export const SheetTitle = DialogPrimitive.Title;
export const SheetDescription = DialogPrimitive.Description;

const sheetVariants = cva(
  "fixed z-overlay flex flex-col gap-4 overflow-y-auto bg-surface p-6 shadow-elevated focus:outline-none data-[state=open]:animate-in data-[state=open]:duration-slow",
  {
    variants: {
      side: {
        left: "inset-y-0 left-0 w-sheet max-w-full data-[state=open]:slide-in-from-left",
        right: "inset-y-0 right-0 w-sheet max-w-full data-[state=open]:slide-in-from-right",
        bottom: "inset-x-0 bottom-0 max-h-dialog rounded-t-xl data-[state=open]:slide-in-from-bottom",
      },
      tone: { light: "", inverse: "bg-surface-inverse text-fg-inverse" },
    },
    defaultVariants: { side: "left", tone: "light" },
  },
);

export const SheetContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & VariantProps<typeof sheetVariants>
>(({ className, children, side, tone, ...props }, ref) => {
  const { t } = useTranslation();
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-overlay bg-overlay data-[state=open]:animate-in data-[state=open]:fade-in-0" />
      <DialogPrimitive.Content ref={ref} className={cn(sheetVariants({ side, tone }), className)} {...props}>
        {children}
        <DialogPrimitive.Close
          className={cn(
            "absolute right-3 top-3 flex h-11 w-11 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus",
            tone === "inverse" ? "text-fg-inverse hover:bg-secondary-hover" : "text-fg-muted hover:bg-surface-muted",
          )}
          aria-label={t("common.close")}
        >
          <X aria-hidden="true" className="h-5 w-5" />
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
});
SheetContent.displayName = "SheetContent";
